import React, { useEffect, useState } from "react";
import { GALLERY, ASSETS } from "../lib/assets";
import SectionHeading from "../components/SectionHeading";
import usePageMeta from "../hooks/usePageMeta";
import { api } from "../lib/api";

// Resolves backend-relative URLs (from Emergent Object Storage /api/files/...) into full URLs.
const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${backendUrl}${u}` : u);

// Reorder gallery so the pitmaster photo is featured up top, everything else follows.
const featured = GALLERY.find((g) => g.src === ASSETS.pitmasterSmoke) ?? GALLERY[0];
const rest = GALLERY.filter((g) => g.src !== featured.src);

export default function Gallery() {
  usePageMeta({
    title: "Gallery & Videos",
    description: "Photos and videos from the pit, the team and the trophies. See competition brisket, catering spreads and family moments from When Pigs Fly Family BBQ.",
    path: "/gallery",
  });
  const [adminPhotos, setAdminPhotos] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/gallery-photos");
        if (!cancelled && Array.isArray(data)) setAdminPhotos(data);
      } catch { /* ignore — page still shows curated photos */ }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <div>
      <section className="border-b border-ink py-16 md:py-24" data-testid="gallery-hero">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <div className="eyebrow mb-3 text-ink/70">Family album</div>
          <h1 className="font-rye text-5xl uppercase leading-[0.9] sm:text-6xl lg:text-7xl">
            Gallery &amp; Videos
          </h1>
          <p className="mt-6 max-w-2xl font-serif text-base leading-relaxed text-ink/80">
            The pit. The team. The trophies. Every image below is from the family — no stock, no staging.
          </p>
        </div>
      </section>

      {/* FEATURED — Pitmaster photo + family tradition video */}
      <section className="border-b border-ink py-16 md:py-24" data-testid="gallery-featured-video">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <figure
            data-testid="gallery-featured-pitmaster"
            className="grain-block relative aspect-[16/9] overflow-hidden border border-ink shadow-hard"
          >
            <img
              src={featured.src}
              alt={featured.alt}
              className="h-full w-full object-cover"
            />
            <figcaption className="absolute bottom-0 left-0 right-0 bg-ink/70 p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-bone">
              {featured.alt}
            </figcaption>
          </figure>

          <div className="mt-16 grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-14">
            <div className="md:col-span-5">
              <div className="eyebrow mb-3 text-ink/70">Our Story · On Film</div>
              <h2 className="font-rye text-4xl uppercase leading-[0.95] sm:text-5xl">
                A Family Tradition of Bold, Fresh Flavor
              </h2>
              <p className="mt-6 font-serif text-base leading-relaxed text-ink/80">
                Food has the power to connect generations. Whether it&rsquo;s learning family recipes
                or the intense memories triggered by the flavors and aromas of our childhood.
              </p>
            </div>
            <div className="md:col-span-7">
              <div
                data-testid="gallery-family-tradition-video"
                className="relative aspect-video w-full overflow-hidden border-2 border-ink shadow-hard"
              >
                <iframe
                  src="https://www.youtube.com/embed/vwuw-Uxixtw"
                  title="A Family Tradition of Bold, Fresh Flavor"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 h-full w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MORE VIDEOS — additional YouTube reels */}
      <section className="border-b border-ink bg-white py-16 md:py-24" data-testid="gallery-more-videos">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="From the pit · On film" title="More Videos" />
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
            {[
              { id: "GSzQjumJtL0", label: "Reel 01" },
              { id: "fTENMQzFDUM", label: "Reel 02" },
              { id: "nBxzqqbzRng", label: "Reel 03" },
            ].map((v) => (
              <div key={v.id} data-testid={`gallery-video-${v.id}`}>
                <div className="relative aspect-video w-full overflow-hidden border-2 border-ink shadow-hard">
                  <iframe
                    src={`https://www.youtube.com/embed/${v.id}`}
                    title={`When Pigs Fly BBQ · ${v.label}`}
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="absolute inset-0 h-full w-full"
                  />
                </div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.24em] text-ink/70">
                  {v.label} · When Pigs Fly BBQ
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-ink py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="Scroll through" title="From The Family" />
          {adminPhotos.length > 0 && (
            <div className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-admin-photos">
              {adminPhotos.map((p, i) => (
                <figure
                  key={p.id}
                  data-testid={`gallery-admin-item-${p.id}`}
                  className={`grain-block relative overflow-hidden border border-ink ${i % 5 === 0 ? "aspect-[4/5]" : i % 3 === 0 ? "aspect-square" : "aspect-[4/3]"}`}
                >
                  <img
                    src={resolveUrl(p.image_url)}
                    alt={p.caption || "Gallery photo"}
                    className="mono-img h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  {p.caption && (
                    <figcaption className="absolute bottom-0 left-0 right-0 bg-ink/70 p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-bone">
                      {p.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          )}
          <div className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((img, i) => (
              <figure
                key={i}
                data-testid={`gallery-item-${i}`}
                className={`grain-block relative overflow-hidden border border-ink ${i % 5 === 0 ? "aspect-[4/5]" : i % 3 === 0 ? "aspect-square" : "aspect-[4/3]"}`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="mono-img h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                />
                <figcaption className="absolute bottom-0 left-0 right-0 bg-ink/70 p-3 font-mono text-[10px] uppercase tracking-[0.2em] text-bone">
                  {img.alt}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
